import axios from 'axios';

import { Address } from 'root/apps/QuoteMachineIntegration/state/v1/Address';
import { PhoneNumberType } from 'root/apps/QuoteMachineIntegration/state/v1/Common';
import { Customer as CustomerPayload } from 'root/apps/QuoteMachineIntegration/state/v1/Customer';
import { EmailAddress } from 'root/apps/QuoteMachineIntegration/state/v1/EmailAddress';
import { PhoneNumber } from 'root/apps/QuoteMachineIntegration/state/v1/PhoneNumber';
import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../../constants';
import { formatRefId } from '../../utils/referenceHelpers';

export interface CreateCreditAccountPayload {
  creditAccountRef: string;
  creditLimit: number;
  currency: string;
  customer: CustomerPayload;
}

export interface CreditAccountResponse {
  id: string;
  creditAccountRef: string;
}

/**
 * Contact email from R-Series customer data.
 */
interface CustomerContactEmail {
  address: string;
  useType?: string;
}

/**
 * Contact phone from R-Series customer data.
 */
interface CustomerContactPhone {
  number: string;
  useType?: string;
}

/**
 * Contact address from R-Series customer data.
 */
interface CustomerContactAddress {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  stateCode?: string;
  zip?: string;
  country?: string;
  countryCode?: string;
}

/**
 * Customer data type from R-Series.
 * This is a flexible interface that matches the runtime structure of
 * saleManager.customer.attributes. Most fields are optional since
 * we only require customerID to build the payload.
 */
export interface CustomerData {
  customerID: string | number;
  firstName?: string;
  lastName?: string;
  company?: string;
  title?: string;
  Contact?: {
    Emails?: { ContactEmail: CustomerContactEmail | CustomerContactEmail[] } | '';
    Phones?: { ContactPhone: CustomerContactPhone | CustomerContactPhone[] } | '';
    Addresses?: { ContactAddress: CustomerContactAddress | CustomerContactAddress[] };
  };
}

/**
 * Builds the payload for creating a credit account on the Invoicing API.
 * This is a shared utility used by both PaymentController.js and CreditAccountService.
 *
 * @param creditAccountId The credit account ID (will be formatted with ls-retail_ prefix)
 * @param creditLimitCents The credit limit in cents (use 0 for no limit)
 * @param customer The customer data from R-Series
 * @param currency The currency code (e.g., 'USD', 'CAD')
 * @returns The formatted payload for POST /v1/credit-accounts
 */
export function buildCreditAccountCreatePayload(
  creditAccountId: string,
  creditLimitCents: number,
  customer: CustomerData,
  currency: string
): CreateCreditAccountPayload {
  // Build emails array
  const emails: EmailAddress[] = [];
  const contactEmails = customer.Contact?.Emails;
  if (contactEmails && contactEmails.ContactEmail) {
    const emailList = Array.isArray(contactEmails.ContactEmail)
      ? contactEmails.ContactEmail
      : [contactEmails.ContactEmail];

    emailList.forEach((emailObj) => {
      if (emailObj?.address) {
        emails.push({
          email: emailObj.address,
          isPrimary: emailObj.useType === 'Primary',
        });
      }
    });
  }

  // Build phoneNumbers array
  const phoneNumbers: PhoneNumber[] = [];
  const contactPhones = customer.Contact?.Phones;
  if (contactPhones && contactPhones.ContactPhone) {
    const phoneList = Array.isArray(contactPhones.ContactPhone)
      ? contactPhones.ContactPhone
      : [contactPhones.ContactPhone];

    phoneList.forEach((phoneObj) => {
      if (phoneObj?.number) {
        let phoneType: PhoneNumberType = 'other';
        if (phoneObj.useType) {
          const useTypeLower = phoneObj.useType.toLowerCase();
          if (['home', 'work', 'mobile'].includes(useTypeLower)) {
            phoneType = useTypeLower as PhoneNumberType;
          }
        }

        phoneNumbers.push({
          type: phoneType,
          phoneNumber: phoneObj.number,
        });
      }
    });
  }

  // Build addresses array
  const addresses: Address[] = [];
  const contactAddresses = customer.Contact?.Addresses;
  if (contactAddresses && contactAddresses.ContactAddress) {
    const addressList = Array.isArray(contactAddresses.ContactAddress)
      ? contactAddresses.ContactAddress
      : [contactAddresses.ContactAddress];

    addressList.forEach((addr) => {
      if (addr) {
        addresses.push({
          addressType: 'contact',
          street1: addr.address1 || '',
          street2: addr.address2 || '',
          city: addr.city || '',
          postalCode: addr.zip || '',
          state: addr.state || '',
          stateCode: addr.stateCode || '',
          country: addr.countryCode || '',
          countryCode: addr.countryCode || '',
        });
      }
    });
  }

  const customerPayload: CustomerPayload = {
    customerRef: formatRefId(`${customer.customerID}`),
    firstName: customer.firstName || '',
    lastName: customer.lastName || '',
    companyName: customer.company || '',
    title: customer.title || '',
    emails,
    phoneNumbers,
    addresses,
  };

  return {
    creditAccountRef: formatRefId(creditAccountId),
    creditLimit: creditLimitCents,
    currency,
    customer: customerPayload,
  };
}

export default class CreditAccountService {
  /**
   * Creates a credit account on the Invoicing side.
   * This is needed when the credit account exists on R-Series but not yet on Invoicing.
   *
   * @param creditAccountId The credit account ID (will be formatted with ls-retail_ prefix)
   * @param creditLimitCents The credit limit in cents (use 0 for no limit)
   * @param customer The customer data from R-Series
   * @param currency The currency code (e.g., 'USD', 'CAD')
   * @returns The created credit account response
   */
  static async createCreditAccount(
    creditAccountId: string,
    creditLimitCents: number,
    customer: CustomerData,
    currency: string
  ): Promise<CreditAccountResponse> {
    const payload = buildCreditAccountCreatePayload(
      creditAccountId,
      creditLimitCents,
      customer,
      currency
    );

    try {
      const url = `${LS_INVOICING_API_ROOT}/credit-accounts`;
      const { data } = await axios.post<CreditAccountResponse>(url, payload);
      return data;
    } catch (error) {
      const creditAccountRef = formatRefId(creditAccountId);
      datadogRum.addError(error, {
        creditAccountRef,
        message: `Failed to create credit account on Invoicing for creditAccountRef: ${creditAccountRef}`,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
